import Navbar               from './neuro/Navbar'
import Feedback             from './neuro/Feedback'
import CharacterCounter     from './neuro/CharacterCounter'
import LanguageDropdown     from './neuro/LanguageDropdown'
import LanguageSwitchButton from './neuro/LanguageSwitchButton'
import SourceA              from './neuro/SourceA' # ContentEditable
import SourceB              from './neuro/SourceB' # Transparent textarea
import Target               from './neuro/Target'
import ToneSelector         from './neuro/ToneSelector'
import Modal                from './neuro/Modal'
import FilesPanel           from './neuro/panels/FilesPanel'
import AppsPanel            from './neuro/panels/AppsPanel'

import Segmenter           from './neuro/helpers/Segmenter'
import StreamingManagement from './neuro/helpers/StreamingManagement'
import TruncateSources     from './neuro/helpers/TruncateSources'

export default class Neuro extends React.PureComponent
  REGULAR_DEBOUNCE_TIME = 300  # in ms
  MOBILE_DEBOUNCE_TIME  = 1000

  MAX_FAVORITE_LANGUAGES = 5
  DEFAULT_TARGET_CODE    = 'en-US' # only if nothing found in browser languages
  FALLBACK_TARGET_CODE   = 'fr'    # to avoid infinite loop sometimes when switching to DEFAULT_TARGET_CODE

  FONT_SIZE_CLASSES =
    biggest:  { min: 0,   max: 59                      }
    bigger:   { min: 60,  max: 179                     }
    normal:   { min: 180, max: 399                     }
    smaller:  { min: 400, max: 799                     }
    smallest: { min: 800, max: Number.MAX_SAFE_INTEGER }
  
  constructor: (props) ->
    super(props)

    @state =
      mode:                    @props.mode
      sources:                 @sourcesFromUrl() #| Arrays of sentences (sources loaded from URL if any)
      targets:                 []                #/
      sourceCode:              @props.sourceCode
      targetCode:              if @props.targetCode.length then @props.targetCode else DEFAULT_TARGET_CODE
      proofreading:            @props.proofreading
      sourceDetection:         @props.sourceDetection
      engine:                  @props.defaultEngine
      tone:                    'auto'
      hiddenTones:             false
      alternatives:            []
      activeSentenceIndex:     -1 #| -1 if no active word/sentence
      activeWordIndex:         -1 #/
      wordAlternatives:        []
      topSourceCodes:          []
      topTargetCodes:          []
      topEngines:              []
      lastLimitedAt:           @props.lastLimitedAt
      limitWarningCode:        @props.limitWarningCode # if empty, warning is not shown
      modalOrigin:             ''
      waitingListFlag:         @props.waitingListFlag
      websocketSubscribed:     false
      loading:                 false
      wordAlternativesLoading: false
      pageReloadAlertVisible:  false
      isFirstRequest:          true

    @requestId  = '0.0'
    @responseId = '0.0'
    @alternativesResponseId = '0.0'

    @wordAlternativesRequestId = '0.0' # no need for response here, we only want to display last one

    # Generate hash to access language from code in O(1)
    @sourceLanguagePerCode = @buildLanguagePerCode(exceptCodes: ['en-US', 'en-GB'])
    @targetLanguagePerCode = @buildLanguagePerCode(exceptCodes: ['en'])

    # Used to put the focus back into the textarea after re-render
    @sourceRef        = React.createRef()
    @targetRef        = React.createRef()
    @targetContentRef = React.createRef()

    # Debounced method
    debounce_time = if @props.mobile then MOBILE_DEBOUNCE_TIME else REGULAR_DEBOUNCE_TIME
    @dTranslate   = _.debounce(@translate, debounce_time)

    # Bound methods
    @changeMode                = @changeMode.bind(this)
    @openModal                 = @openModal.bind(this)
    @scrollToPricing           = @scrollToPricing.bind(this)
    @openCheckoutModal         = @openCheckoutModal.bind(this)
    @updateWaitingListFlag     = @updateWaitingListFlag.bind(this)
    @updateSourcesAndTranslate = @updateSourcesAndTranslate.bind(this)
    @selectSourceCode          = @selectSourceCode.bind(this)
    @selectTargetCode          = @selectTargetCode.bind(this)
    @exitProofreading          = @exitProofreading.bind(this)
    @clearSource               = @clearSource.bind(this)
    @switchLanguages           = @switchLanguages.bind(this)
    @selectAlternative         = @selectAlternative.bind(this)
    @selectTone                = @selectTone.bind(this)
    @hideTones                 = @hideTones.bind(this)
    @showTones                 = @showTones.bind(this)
    @selectEngine              = @selectEngine.bind(this)
    @removeTopEngine           = @removeTopEngine.bind(this)
    @focusSource               = @focusSource.bind(this)
    @setActiveSentence         = @setActiveSentence.bind(this)
    @setActiveWord             = @setActiveWord.bind(this)
    @selectWordAlternative     = @selectWordAlternative.bind(this)
    @selectTargetText          = @selectTargetText.bind(this)
    @unselectTargetText        = @unselectTargetText.bind(this)
    @isProofreadingAllowed     = @isProofreadingAllowed.bind(this)

  componentDidMount: ->
    # "didMount" because localStorage is not available when prerendering
    @loadTopLanguageCodes( =>
      @setState(
        targetCode: if @props.targetCode.length then @props.targetCode else @state.topTargetCodes[0] # Use targetCode from URL (props) of top language from list
      , =>
        @updateSourcesAndTranslate(@props.sourceText, { debounced: false }) if @props.sourceText.length
      )
    )

    @cleanUrl()
    @loadEngines()

    @bindCopyAndCut()
    @bindVisibilityChange()
    @bindWebsockets()
    @bindShortcuts()

  # URL source text (string) is passed as props from mac/win client
  sourcesFromUrl: ->
    if @props.sourceText.length
      Segmenter.sentences(@props.sourceText, @props.sourceCode)
    else
      []

  cleanUrl: ->
    params = new URLSearchParams(window.location.search)

    if params.has('client')
      params.delete('client')
      history.replaceState({}, '', "#{location.pathname}?#{params}")

  buildLanguagePerCode: (options = { exceptCodes: [] }) ->
    # Build hash
    languagePerCode = {}
    @props.languages.forEach((language) => languagePerCode[language.code] = language)

    # Remove extra codes
    options.exceptCodes.forEach((code) => delete languagePerCode[code])

    languagePerCode

  loadTopLanguageCodes: (callback) ->
    # Top languages from localStorage
    topSourceCodes = localStorage.getItem('top-source-codes')?.split(',') || []
    topTargetCodes = localStorage.getItem('top-target-codes')?.split(',') || []

    # Top languages from browser
    browserLanguageCodes = navigator.languages || []
    browserLanguageCodes = browserLanguageCodes.map((language) => [language, language.split('-')[0]]).flat() # Be sure to add 'fr' after 'fr-FR' in navigator.languages when it's not there (it happens, and we saw ['fr-FR', 'be-AE', 'be', 'fr-FR', 'fr'] in the wild)
    browserLanguageCodes = _.uniq(browserLanguageCodes)

    # Merge them with priority of localStorage
    topSourceCodes = _.uniq(topSourceCodes.concat(browserLanguageCodes))
    topTargetCodes = _.uniq(topTargetCodes.concat(browserLanguageCodes))

    # Remove non-accepted languages
    topSourceCodes = topSourceCodes.filter((code) => @sourceLanguagePerCode[code])
    topTargetCodes = topTargetCodes.filter((code) => @targetLanguagePerCode[code])

    # Keep max languages
    topSourceCodes = topSourceCodes.slice(0, MAX_FAVORITE_LANGUAGES)
    topTargetCodes = topTargetCodes.slice(0, MAX_FAVORITE_LANGUAGES)

    # Set default if no target codes
    topTargetCodes = [DEFAULT_TARGET_CODE] if topTargetCodes.length == 0

    @setState(
      topSourceCodes: topSourceCodes
      topTargetCodes: topTargetCodes
    , callback)

  loadEngines: ->
    # Load current engine first, because we'll need it for Top engines too
    engine = localStorage.getItem('engine')

    # Fallback to default engine
    if !engine || !@props.engines[engine] || !@props.engines[engine].selectable
      engine = @props.defaultEngine

    # Load top engines from local storage, filtering out any invalid engine
    topEngines = localStorage.getItem('top-engines')?.split(',') || []
    topEngines = topEngines.filter((key) => @props.engines[key])

    # Fallback to default (UI-suggested) if none in local storage
    if !topEngines.length
      topEngines = Object.keys(@props.engines).filter((key) => @props.engines[key].pinned)

    # Add currently selected engine in first position if not present
    topEngines.unshift(engine) if !topEngines.includes(engine)

    @setState(
      engine:     engine
      topEngines: topEngines
    )

  saveTopLanguageCodes: ->
    localStorage.setItem('top-source-codes', @state.topSourceCodes.join(','))
    localStorage.setItem('top-target-codes', @state.topTargetCodes.join(','))

  addTopCode: (code, type) ->
    if code.length
      topCodeType = "top#{_.capitalize(type)}Codes"

      topCodes = [code].concat(@state[topCodeType])        # Add new language in first position
      topCodes = _.uniq(topCodes)                          # Remove duplicates
      topCodes = topCodes.slice(0, MAX_FAVORITE_LANGUAGES) # Keep only the first x (see MAX)

      @setState(
        "#{topCodeType}": topCodes
      , @saveTopLanguageCodes)

  addTopSourceCode: (code) ->
    @addTopCode(code, 'source')

  addTopTargetCode: (code) ->
    @addTopCode(code, 'target')

  saveTopEngines: ->
    localStorage.setItem('top-engines', @state.topEngines.join(','))

  saveSelectedEngine: (engine) ->
    localStorage.setItem('engine', engine)

  addTopEngine: (engine) ->
    @setState(
      topEngines: _.uniq([...@state.topEngines, engine])
    , @saveTopEngines)

  removeTopEngine: (engine) ->
    @setState(
      topEngines: _.without(@state.topEngines, engine)
    , @saveTopEngines)

  focusSource: ->
    if @state.mode == 'text'
      @sourceRef.current.focus({ preventScroll: true }) if @sourceRef.current

  sentencesLength: (sentences) ->
    _.sumBy(sentences, (sentence) -> sentence.length)

  sourceLength: ->
    @sentencesLength(@state.sources)

  targetLength: ->
    @sentencesLength(@state.targets)

  # Truncate to last sentence
  truncateForTranslation: (sentences) ->
    service = new TruncateSources(sentences, @props.charLimit, @props.truncatingToleranceLength)
    service.run()

  changeMode: (mode) ->
    newSourceCode = if @state.sourceCode.length && !@state.sourceDetection then @state.sourceCode else ''      # If source language was "forced", we keep it
    newTargetCode = if @state.proofreading then @mostPlausibleTargetCode(newSourceCode) else @state.targetCode # If proofreading, be sure to select a different target language

    @setState(
      mode:             mode
      proofreading:     false
      sources:          []
      targets:          []
      sourceCode:       newSourceCode
      targetCode:       newTargetCode
      alternatives:     []
      limitWarningCode: ''
      sourceDetection:  !newSourceCode.length
    , =>
      history.pushState({}, '', "/#{@props.currentLocale}/#{@state.mode}")
      @updateMetaTags()
      @updateMarketingSubTitle()
      @translate() # we now it won't translate empty string, but it will deal with requestId/responseId and avoid bugs
      @focusSource()
    )

  updateMetaTags: ->
    $('title').text(@props.i18n.page[@state.mode].title)
    $('meta[name=description]').attr('content', @props.i18n.page[@state.mode].description)
    $('meta[name=keywords]').attr('content', @props.i18n.page[@state.mode].keywords)

  updateMarketingSubTitle: ->
    element = $('.marketing-section .introduction .subtitle')

    if element.length
      titlePerMode = element.data('modes')
      newTitle     = titlePerMode[@state.mode]

      element.text(newTitle)

  scrollToPricing: (key = '') ->
    window.Pricing.scrollAndFlashCta(key)

  openCheckoutModal: ->
    window.Pricing.openCheckoutModal()

  openModal: (origin, e) ->
    e.preventDefault() if e

    @setState({
      modalOrigin: origin
    }, =>
      modal = Bootstrap.Modal.getOrCreateInstance('#modal-neuro')
      modal.show()
    )

  updateWaitingListFlag: (flag) ->
    @setState(waitingListFlag: flag)

  updateSourcesAndTranslate: (sourceText, options = { debounced: true }) ->
    sources = Segmenter.sentences(sourceText, @state.sourceCode)

    # Be sure to close word_alternative menu
    @setActiveWord(-1)

    if sources.length
      @setState({
        sources: sources,
      }, =>
        if options.debounced
          @dTranslate()
        else
          @translate()
      )
    else
      @clearSource()

  clearSource: (e) ->
    # If source code was "forced", we keep it
    newSourceCode = if @state.sourceCode.length && !@state.sourceDetection then @state.sourceCode else ''

    @setState(
      sources:          []
      targets:          []
      sourceCode:       newSourceCode
      alternatives:     []
      limitWarningCode: ''
    , =>
      @translate() # we now it won't translate empty string, but it will deal with requestId/responseId and avoid bugs
      @focusSource()
    )

  exitProofreading: ->
    if @state.proofreading
      @setState({
        proofreading:    false
        sourceDetection: true
        targetCode:      @mostPlausibleTargetCode(@state.sourceCode)
      }, @translate)

  # Special cases where source code doesn't exist as target code, we need to use best equivalent
  sourceCodeToTargetCode: (sourceCode) ->
    if sourceCode == 'en'
      @state.topTargetCodes.concat(['en-US']).find((targetCode) => ['en-US', 'en-GB'].includes(targetCode))
    else
      sourceCode

  # Special cases where target code doesn't exist as source code, we need to use best equivalent
  targetCodeToSourceCode: (targetCode) ->
    if ['en-US', 'en-GB'].includes(targetCode)
      'en'
    else
      targetCode

  switchLanguages: (e) ->
    if @hasSourceLanguage()
      @setState(
        sources:         @state.targets
        targets:         [] # We choose not to display anything, to avoid a flashing effect before the new translation
        sourceCode:      @targetCodeToSourceCode(@state.targetCode)
        targetCode:      @sourceCodeToTargetCode(@state.sourceCode)
        sourceDetection: @state.sourceDetection # we don't change it! We want the source to stay auto-detected if it was!
        alternatives:    []
      , =>
        @updateSourcesAndTranslate(@state.sources.join(''), { debounced: false }) # Will re-segment text according to source language
      )

  sourceLanguageDirection: ->
    @languageDirection(@sourceLanguagePerCode[@state.sourceCode])

  targetLanguageDirection: ->
    @languageDirection(@targetLanguagePerCode[@state.targetCode])

  languageDirection: (language) ->
    # No language is passed when the source language is still on "auto-detect",
    # so we let the browser decide automatically from the first characters
    # (it's faster than waiting for the language-detection response)
    if !language
      'auto'
    else if language.rightToLeft
      'rtl'
    else
      'ltr'

  selectAlternative: (targets) ->
    # Remove selected alternative from list
    newAlternatives = @state.alternatives.filter((alternativeTargets) =>
      !_.isEqual(alternativeTargets, targets)
    )

    # Add current targets in alternatives
    newAlternatives.push(@state.targets)

    @setState(
      targets:      targets
      alternatives: newAlternatives
    , =>
      @runCssAnimationOnRef(@targetContentRef, 'flashed')
    )

  selectTone: (tone) ->
    @setState(
      tone: tone
    , @translate)

  hideTones: ->
    @setState(hiddenTones: true)

  showTones: ->
    @setState(hiddenTones: false)

  selectEngine: (engine) ->
    if engine != @state.engine
      @saveSelectedEngine(engine)

      @setState(
        engine: engine
      , =>
        @translate()
        @addTopEngine(engine)
      )

  # We don't necessarily want to apply the response from the backend.
  # if the source is too short and the language too random.
  detectSourceCode: (responseSourceCode) ->
    if responseSourceCode == ''
      '' # If backend says "I don't know", we accept it!
    else
      # Byte length of source, because detection on 1 or 2 Chinese characters is enough
      # "hello" => 5  (English)
      # "блять" => 10 (Russian)
      # "你好"   => 6  (Chinese)
      sourceByteLength = _.sumBy(@state.sources, (source) -> new Blob([source]).size)

      if sourceByteLength >= 5 && @state.topSourceCodes.includes(responseSourceCode)
        responseSourceCode # We are quite confident this is the language we want (a few bytes are enough if language is favorite)
      else if sourceByteLength >= 7
        responseSourceCode # This language is weird, but enough bytes to convince us that's the one we want
      else
        @state.sourceCode  # If we are not sure of the new language, we keep the old one!

  mostPlausibleTargetCode: (sourceCode) ->
    @state.topTargetCodes.concat([DEFAULT_TARGET_CODE, FALLBACK_TARGET_CODE]) # Fallback target code to avoid infinite loop when no other codes available
                         .find((targetCode) => !@sameCodePrefix(sourceCode, targetCode))

  adaptedTargetCodeState: (sourceCode) ->
    if @sameCodePrefix(sourceCode, @state.targetCode) && !@state.proofreading
      if @state.sourceCode == '' # If previous source code is "Detect Language", use another preferred language for target
        { targetCode: @mostPlausibleTargetCode(sourceCode) }
      else
        { targetCode: @sourceCodeToTargetCode(@state.sourceCode) } # use previous source language as target target (switch them)

  proofreadingState: (sourceCode, targetCode) ->
    if @isProofreadingAllowed() && @sameCodePrefix(sourceCode, targetCode)
      {
        sourceCode:      sourceCode
        targetCode:      targetCode
        proofreading:    true
        sourceDetection: false
      }

  selectSourceCode: (code) ->
    @setState({
      sourceCode:      code
      sourceDetection: code == ''      # Enable language selection only if "Detect Language" is selected
      ...@adaptedTargetCodeState(code) # Adapt target language if needed (when same code selected)
    }, =>
      @addTopSourceCode(code)
      @updateSourcesAndTranslate(@state.sources.join(''), { debounced: false }) # Will re-segment text according to source language
    )

  selectTargetCode: (code) ->
    @setState({
      targetCode: code
      ...@proofreadingState(@state.sourceCode, code)
    }, =>
      @addTopTargetCode(@state.targetCode)
      @translate()
    )

  setActiveSentence: (index, callback) ->
    @setState(activeSentenceIndex: index, callback)

  setActiveWord: (index, word) ->
    if index == -1 # unselect active word
      @setState(
        activeWordIndex:  -1
        wordAlternatives: []
      )
    else
      source = @state.sources[@state.activeSentenceIndex].trim()
      target = @state.targets[@state.activeSentenceIndex].trim()

      # Replace selected word in target as [[word]] for backend service
      targetWords        = Segmenter.words(target, @state.targetCode)
      replacedWord       = targetWords[index]
      targetWords[index] = "[[#{word}]]" # We use the 'word' from the click, not the one from the targetWords[index], in case there is a discrepancy between Segmenter.words and the index from rendering (it could happen because of diffs during rendering)

      if word != replacedWord
        Bugsnag.notify("WordAlternatives: selected word doesn't match with the selected index: #{replacedWord} - #{word} - #{targetWords.join('')}")

      @wordAlternativesRequestId = @incrementVersion(@wordAlternativesRequestId)

      params =
        source:             source
        target:             targetWords.join('')
        originalWord:       word
        sourceLanguageCode: @state.sourceCode # TODO: should we ignore situations when sourceCode == '' ? (not detected... yet)
        targetLanguageCode: @state.targetCode
        engine:             @state.engine
        sessionUid:         @props.sessionUid
        requestId:          @wordAlternativesRequestId

      @setState(
        activeWordIndex:         index
        wordAlternativesLoading: true
      , =>
        http.post("/word_alternatives.json", params, (data) =>
          if !data.enqueued # Response will come asynchronously with websockets, we can ignore the response now.
            console.log('TRANSLATION ACTION WAS NOT CORRECTLY ENQUEUED')
        )
      )

  selectWordAlternative: (wordAlternative) ->
    # 1. Only keep sentences before selected one as preTargets
    preTargets = @state.targets.slice(0, @state.activeSentenceIndex)

    # 2. Replace word in current target sentence using [[]]
    currentTarget      = @state.targets[@state.activeSentenceIndex]
    currentTargetWords = Segmenter.words(currentTarget, @state.targetCode)
    originalWord       = currentTargetWords[@state.activeWordIndex]
    currentTargetWords[@state.activeWordIndex] = "[[#{originalWord}]]"

    # 3. Remove end of last sentence
    currentTargetWords = currentTargetWords.slice(0, @state.activeWordIndex + 1)
    currentTarget      = currentTargetWords.join('')

    @setState(targets: preTargets.concat([currentTargetWords.slice(0, @state.activeWordIndex).join('')]))

    @requestId = @incrementVersion(@requestId)
    @startLoading()

    params =
      sources:            @truncateForTranslation(@state.sources)
      preTargets:         preTargets        # What's before the selected sentence
      currentTarget:      currentTarget     # The current sentence ending with "[[originalWord]]"
      wordAlternative:    wordAlternative   # The selected word alternative to continue the sentence
      sourceLanguageCode: @state.sourceCode # TODO: should we ignore cases when sourceCode == '' ? (not detected... yet)
      targetLanguageCode: @state.targetCode
      tone:               @state.tone
      engine:             @state.engine
      sessionUid:         @props.sessionUid
      requestId:          @requestId

    @setState(
      activeWordIndex:     -1,
      activeSentenceIndex: -1,
      wordAlternatives:    []
    , =>
      http.post("/apply_word_alternative.json", params, (data) =>
        if !data.enqueued # Response will come asynchronously with websockets, we can ignore the response now.
          console.log('TRANSLATION ACTION WAS NOT CORRECTLY ENQUEUED')
      )
    )

  emptySource: ->
    @state.sources.length == 0 || @state.sources.every((sentence) -> sentence.trim().length == 0)

  sameCodePrefix: (sourceCode, targetCode) ->
    same              = sourceCode == targetCode
    sameEnglishPrefix = sourceCode == 'en' && sourceCode == targetCode.split('-')[0] # we don't want Chinese to became the same

    same || sameEnglishPrefix

  sameLanguages: ->
    @sameCodePrefix(@state.sourceCode, @state.targetCode)

  isProofreadingAllowed: ->
    @state.mode == 'text'

  hasSourceLanguage: ->
    @state.sourceCode != ''

  canSwitchLanguages: ->
    @hasSourceLanguage() && !@sameLanguages()  && !@state.loading

  translate: ->
    @requestId = @incrementVersion(@requestId)

    if @emptySource()
      @responseId             = @requestId
      @alternativesResponseId = @requestId

      @stopLoading()

      @setState(
        targets:          []
        alternatives:     []
        limitWarningCode: ''
      )
    else
      @startLoading()

      params =
        sources:            @truncateForTranslation(@state.sources)
        sourceLanguageCode: if @state.sourceDetection then '' else @state.sourceCode
        targetLanguageCode: @state.targetCode
        tone:               @state.tone
        engine:             @state.engine
        sessionUid:         @props.sessionUid
        requestId:          @requestId
        client:             if @state.isFirstRequest then @props.client else ''

      @waitWebsocketSubscribed().then( =>
        http.post("/translate.json", params, (data) =>
          if data.enqueued # Response will come asynchronously with websockets, we can ignore the response now.
            # nothing, response will come asynchronously with websockets.
            @setState(isFirstRequest: false) # LLM requests are always enqueued
          else
            @applyTranslateResponse(data.response) # When no need to enqueue (detection enabled and same languages detected. faster response to allow frontend to update languages and start another request)
        , @translateFail.bind(this))
      ).catch((error) => console.log('Error during /translate.json', error));

  translateFail: (jqXHR, textStatus, errorThrown) ->
    switch jqXHR.status
      when 422
        @checkSessionStatus()
      when 429
        if jqXHR.responseJSON.errorType == 'limit_exceeded'
          @setState(
            lastLimitedAt:    jqXHR.responseJSON.lastLimitedAt
            limitWarningCode: jqXHR.responseJSON.limitWarningCode
          , @stopLoading)
      when 0 # Connection error (no Internet?)
        # TODO: create small stacked notification (that will disappear automatically) to warn the user
      else
        if errorThrown.length
          Bugsnag.notify(new Error('Ajax error after translate'), (event) =>
            event.addMetadata('Backend response', jqXHR)
            event.addMetadata('Ajax Error', errorThrown)
          )

  checkSessionStatus: ->
    if !@state.pageReloadAlertVisible
      params =
        signed_in: !!@props.currentUser

      http.get("/session_status.json", params, (data) =>
        if !data.valid
          # Auto-reload page or show error to the user
          if @emptySource()
            Bugsnag.notify('Invalid token, page will auto-reload.')
            location.reload()
          else
            Bugsnag.notify('Invalid token, user will be warned.')
            @setState(pageReloadAlertVisible: true)
      )

  bindCopyAndCut: ->
    for action in ['copy', 'cut']
      document.addEventListener(action, (e) =>
        inNeuroTarget = $(e.target).closest('#target-content').length
        selection     = window.getSelection().toString()

        if inNeuroTarget && selection.length
          # Using 'text/plain' to prevent richtext format messing up line breaks
          e.clipboardData.setData('text/plain', selection)
          e.preventDefault()

          window.TargetCopyButton.showSuccessPopover()
      )

  bindVisibilityChange: ->
    document.addEventListener('visibilitychange', (event) =>
      # Check CSRF token validity when page is visible again (switching tabs in browser)
      @checkSessionStatus() if document.visibilityState == 'visible'
    )

  bindWebsockets: ->
    testEnvSuffix = if @props.environment == 'test' then "-test#{@props.testEnvNumber}" else ''
    channel       = Pusher.instance.subscribe("public-neuro-#{@props.sessionUid}#{testEnvSuffix}")

    # Receive new translation
    channel.bind('translate', (data) =>
      data = humps.camelizeKeys(data)

      if data.payloadIdentifier # payload too big for websockets, need to make an extra request to fetch it
        http.get("/fetch_payload.json", { identifier: data.payloadIdentifier }, (httpData) =>
          @applyTranslateResponse(httpData)
        )
      else
        @applyTranslateResponse(data)
    )

    # Receive new alternatives
    channel.bind('alternatives', (data) =>
      data = humps.camelizeKeys(data)
      @applyAlternativesResponse(data)
    )

    # Receive new word alternatives
    channel.bind('word_alternatives', (data) =>
      data = humps.camelizeKeys(data)
      @applyWordAlternativeResponse(data)
    )

    channel.bind('pusher:subscription_succeeded', (data) =>
      @setState(websocketSubscribed: true)
    )

  waitWebsocketSubscribed: ->
    if @state.websocketSubscribed
      Promise.resolve()
    else
      new Promise((resolve) =>
        checkInterval = setInterval( =>
          if @state.websocketSubscribed
            clearInterval(checkInterval)
            resolve()
        , 50)
      )

  charLimitWarningCode: ->
    if @props.currentUser?.isPro
      'pro_char'
    else if @props.currentUser?.isFree
      'user_char'
    else
      'visitor_char'

  applyTranslateResponse: (data) ->
    if data.requestId && @isBiggerVersion(data.requestId, @responseId)
      @responseId = data.requestId

      streamingManagement = new StreamingManagement(
        { # current state
          id:      @requestId,
          sources: @state.sources
          targets: @state.targets
        },
        { # latest request
          id:      data.requestId,
          sources: data.sources, # empty for now!
          targets: data.targets
        },
        data.completion # 'partial' / 'sentence' / 'complete''
      )

      newTargets           = streamingManagement.bestTargets()
      lastRequestCompleted = @isLastRequest() && data.completion == 'complete'

      if lastRequestCompleted
        @stopLoading()

      # Check if the character limit in the source was reached (newTargets.length to avoid warning message when language detection triggers switching)
      if lastRequestCompleted && newTargets.length && @sourceLength() > @props.charLimit
        limitWarningCode = @charLimitWarningCode()
      else
        limitWarningCode = ''

      # Remove alternatives if they don't match the latest requestId anymore
      if @isBiggerVersion(@requestId, @alternativesResponseId)
        alternativesState = { alternatives: [] }

      previousTargetCode = @state.targetCode
      detectedSourceCode = @detectSourceCode(data.sourceLanguageCode)

      @setState({
        targets:          newTargets
        sourceCode:       detectedSourceCode
        limitWarningCode: limitWarningCode
        ...alternativesState
        ...@adaptedTargetCodeState(detectedSourceCode) # Adapt target language if needed (when same code detected)
      }, =>
        @translate() if @state.targetCode != previousTargetCode
      )

  applyAlternativesResponse: (data) ->
    if data.requestId && @isBiggerVersion(data.requestId, @alternativesResponseId)
      @alternativesResponseId = data.requestId

      # Only keep alternatives that are not the current targets
      # => May be open to race conditions but alternatives should be slower than translation.
      # => Even if it doesn't work, it's also filtered out at the `render` (could glitch when fading-out but it's not so bad)
      alternatives = data.alternatives.filter((alternativeTargets) =>
        !_.isEqual(@state.targets, alternativeTargets)
      )

      @setState(
        alternatives: alternatives
      )

  applyWordAlternativeResponse: (data) ->
    if data.requestId && data.requestId == @wordAlternativesRequestId
      @setState(
        wordAlternatives:        if data.wordAlternatives.length then data.wordAlternatives else [data.originalWord]
        wordAlternativesLoading: false
      )

  bindShortcuts: ->
    ctrlModifier = if navigator.platform.includes('Mac') then 'meta' else 'ctrl'

    # Can be used anywhere on the page except in actual input fields
    $(document).on('keydown', null, "#{ctrlModifier}+a", @selectTargetText)

  selectTargetText: (e) ->
    # Only works in 'text' mode (not 'files' and 'apps')
    if @state.mode == 'text'
      e.preventDefault() if e
    
      targetArea    = @targetRef.current
      targetContent = @targetContentRef.current

      targetArea.focus()

      range = document.createRange()
      range.setStart(targetContent, 0)
      range.setEnd(targetContent, targetContent.childNodes.length)

      selection = window.getSelection()
      selection.removeAllRanges()
      selection.addRange(range)

  unselectTargetText: (e) ->
    selection = window.getSelection()
    selection.removeAllRanges()

  fontSizeClass: ->
    longestLength = Math.max(@sourceLength(), @targetLength())

    fontSizeClass = Object.entries(FONT_SIZE_CLASSES).find(([k, bounds]) =>
      bounds.min <= longestLength <= bounds.max
    )[0]

    fontSizeClass += " ios" if @props.ios

    fontSizeClass

  isBiggerVersion: (version1, version2) ->
    splitted1 = version1.split('.')
    splitted2 = version2.split('.')

    firstNumber1 = parseInt(splitted1[0])
    firstNumber2 = parseInt(splitted2[0])
    lastNumber1  = parseInt(splitted1[1])
    lastNumber2  = parseInt(splitted2[1])

    firstNumber1 > firstNumber2 || (firstNumber1 == firstNumber2 && lastNumber1 > lastNumber2)

  # Last request but not necessarily last websocket
  isLastRequest: ->
    !@isBiggerVersion(@requestId, @responseId)

  # increment first number only (second number is for websockets)
  incrementVersion: (version) ->
    newVersion = parseInt(version.split('.')[0]) + 1
    "#{newVersion}.0"

  startLoading: ->
    @setState(loading: true)
    @startLoadingTitleAnimation()

  stopLoading: ->
    @setState(loading: false)
    @stopLoadingTitleAnimation()

  startLoadingTitleAnimation: ->
    @titleAnimation ||= setInterval( =>
      ooos = ['ooo', 'Ooo', 'oOo', 'ooO']

      if document.title.includes(ooos[0])
        document.title = document.title.replace(ooos[0], ooos[1])
      else if document.title.includes(ooos[1])
        document.title = document.title.replace(ooos[1], ooos[2])
      else if document.title.includes(ooos[2])
        document.title = document.title.replace(ooos[2], ooos[3])
      else if document.title.includes(ooos[3])
        document.title = document.title.replace(ooos[3], ooos[0])
    , 250) # faster than that and Chrome won't refresh quickly enough (the number of refrehes seems limited)

  stopLoadingTitleAnimation: ->
    clearInterval(@titleAnimation)
    @titleAnimation = null

    document.title = document.title.replace('Ooo', 'ooo') # go back to original capitalization
                                   .replace('oOo', 'ooo')
                                   .replace('ooO', 'ooo')

  runCssAnimationOnRef: (ref, cssClass) ->
    if ref.current
      element   = $(ref.current)
      eventName = "animationend.#{cssClass}" # Namespaced event (jQuery)

      element.off(eventName)             # Remove event listener
             .removeClass(cssClass)      # Remove animation class
             .each(-> this.offsetHeight) # Hack to force reflow and ensure animation restart
             .addClass(cssClass)         # Add animation class
             .one(eventName, ->          # Add event listener
               element.removeClass(cssClass)
             )
  render: ->
    <>
      { @renderHeader() }
      { @renderInterface() }
      { @renderModal()  }
    </>

  renderHeader: ->
    <Navbar mode={@state.mode}
            currentUser={@props.currentUser}
            currentLocale={@props.currentLocale}
            i18n={@props.i18n.navbar}
            paths={@props.paths}
            blogNotification={@props.blogNotification}
            changeMode={@changeMode} />

  renderInterface: ->
    containerClasses = "container-lg neuro-container #{@state.mode}-mode"
    containerClasses += " expanded-container" if @props.expandedContainer
    containerClasses += " pro-user"   if @props.currentUser?.isPro

    cardClasses = "neuro card"
    cardClasses += " rainbow-outline rainbow-softer" if @props.currentUser?.isPro

    <div className={containerClasses}>
      <div className={cardClasses}>
        { @renderToolbar() }
        { @renderPanel() }
        { @renderPageReloadAlert() }
      </div>

      <Feedback currentLocale={@props.currentLocale}
                currentUser={@props.currentUser}
                paths={@props.paths}
                i18n={@props.i18n.feedback} />

      { @renderScrollIconArea() }
    </div>

  renderScrollIconArea: ->
    if @props.currentUser
      href       = "#pricing"
      arialLabel = @props.i18n.scrollIconAriaLabel.pricing
    else
      href       = "#about-neuro"
      arialLabel = @props.i18n.scrollIconAriaLabel.about

    <div className="scroll-icon-area">
      <a href={href} aria-label={arialLabel}>
        <i className="fas fa-angles-down fa-fade"></i>
      </a>
    </div>

  renderToolbar: ->
    classes = "row row-cols-2 row-toolbar"
    classes += " proofreading" if @state.proofreading

    if @state.mode in ['text', 'files']
      <div className={classes} key="toolbar">
        <div className="col col-toolbar col-toolbar-source">
          { @renderProofreadingTitle() }
          <LanguageDropdown placeholder={@props.i18n.languageDropdown.sourcePlaceholder}
                            languageCode={@state.sourceCode}
                            oppositeCode={@state.targetCode}
                            topCodes={@state.topSourceCodes}
                            maxCodes={MAX_FAVORITE_LANGUAGES}
                            languagePerCode={@sourceLanguagePerCode}
                            autoDetectOption={true}
                            detection={@state.sourceDetection}
                            proofreading={@state.proofreading}
                            onSelectLanguage={@selectSourceCode}
                            focusSource={@focusSource}
                            sameCodePrefix={@sameCodePrefix}
                            isProofreadingAllowed={@isProofreadingAllowed}
                            i18n={@props.i18n.languageDropdown} />
        </div>
        <div className="col col-toolbar col-toolbar-target">
          <LanguageDropdown placeholder={@props.i18n.languageDropdown.targetPlaceholder}
                            languageCode={@state.targetCode}
                            oppositeCode={@state.sourceCode}
                            topCodes={@state.topTargetCodes}
                            maxCodes={MAX_FAVORITE_LANGUAGES}
                            languagePerCode={@targetLanguagePerCode}
                            autoDetectOption={false}
                            detection={false}
                            proofreading={@state.proofreading}
                            onOpen={@hideTones}
                            onClose={@showTones}
                            exitProofreading={@exitProofreading}
                            onSelectLanguage={@selectTargetCode}
                            focusSource={@focusSource}
                            sameCodePrefix={@sameCodePrefix}
                            isProofreadingAllowed={@isProofreadingAllowed}
                            i18n={@props.i18n.languageDropdown} />
          { @renderToneSelector() }
        </div>
        <LanguageSwitchButton disabled={!@canSwitchLanguages()}
                              onClick={@switchLanguages} />
      </div>

  renderProofreadingTitle: ->
    <div className="proofreading-title">
      <h4>
        <i className="fas fa-pen-clip me-2"></i>
        {@props.i18n.toolbar.proofreadingTitle}
      </h4>
    </div>

  renderToneSelector: ->
    if @state.mode == 'text'
      <ToneSelector tone={@state.tone}
                    tones={@props.tones}
                    hidden={@state.hiddenTones}
                    onChange={@selectTone}
                    focusSource={@focusSource}
                    i18n={@props.i18n.toneSelector} />

  renderPanel: ->
    switch @state.mode
      when 'text'  then @renderTranslationPanel()
      when 'files' then @renderFilesPanel()
      when 'apps'  then @renderAppsPanel()

  renderTranslationPanel: ->
    classes = "row row-cols-1 row-cols-md-2 row-panel"
    classes += " empty-source" if @emptySource() # This allows us to hide the target panel and the separator line on mobile view

    <div className={classes} key="translation">
      <div className="col col-source">
        {@renderSource()}
        {@renderClearSourceButton()}
        <CharacterCounter charCount={@sourceLength()}
                          charLimit={@props.charLimit}
                          scrollToPricing={@scrollToPricing} />
      </div>
      <div className="col col-target">
        {@renderTarget()}
      </div>
    </div>

  renderFilesPanel: ->
    classes = "row row-cols-1 row-panel"

    <div className={classes} key="files">
      <FilesPanel openModal={@openModal}
                  i18n={@props.i18n.filesPanel} />
    </div>

  renderAppsPanel: ->
    classes = "row row-cols-1 row-panel"

    <div className={classes} key="apps">
      <AppsPanel assets={@props.assets}
                 i18n={@props.i18n.appsPanel} />
    </div>

  renderPageReloadAlert: ->
    if @state.pageReloadAlertVisible
      <div id="page-reload-alert" className="card">
        <button className="btn-close"
                onClick={() => @setState(pageReloadAlertVisible: false)}>
        </button>
        <strong>
          {@props.i18n.pageReloadAlert.title}
        </strong>
        <span>
          {@props.i18n.pageReloadAlert.instruction}
        </span>
        <a className="reload"
           href="#"
           onClick={(e) => e.preventDefault(); location.reload()}>
          { @props.i18n.pageReloadAlert.reload }
        </a>
      </div>

  renderClearSourceButton: ->
    if @sourceLength()
      <button className="btn-close btn-clear-source"
              onClick={@clearSource}
              tabIndex="-1">
      </button>

  renderSource: ->
    placeholder = if @state.proofreading then @props.i18n.source.proofreadingPlaceholder else @props.i18n.source.placeholder

    if @props.abTesting == 'a'
      <SourceA sourceRef={@sourceRef}
               sources={@state.sources}
               placeholder={placeholder}
               direction={@sourceLanguageDirection()}
               languageCode={@state.sourceCode}
               fontSizeClass={@fontSizeClass()}
               activeSentenceIndex={@state.activeSentenceIndex}
               updateSourcesAndTranslate={@updateSourcesAndTranslate}
               i18n={@props.i18n.source} />
    else
      <SourceB sourceRef={@sourceRef}
               sources={@state.sources}
               placeholder={placeholder}
               direction={@sourceLanguageDirection()}
               languageCode={@state.sourceCode}
               fontSizeClass={@fontSizeClass()}
               activeSentenceIndex={@state.activeSentenceIndex}
               updateSourcesAndTranslate={@updateSourcesAndTranslate}
               i18n={@props.i18n.source} />

  renderTarget: ->
    <Target targetRef={@targetRef}
            targetContentRef={@targetContentRef}
            targets={@state.targets}
            proofreading={@state.proofreading}
            sources={@state.sources} # for proofreading diff
            languageCode={@state.targetCode}
            direction={@targetLanguageDirection()}
            loading={@state.loading}
            alternatives={@state.alternatives}
            wordAlternatives={@state.wordAlternatives}
            wordAlternativesLoading={@state.wordAlternativesLoading}
            fontSizeClass={@fontSizeClass()}
            activeSentenceIndex={@state.activeSentenceIndex}
            activeWordIndex={@state.activeWordIndex}
            engine={@state.engine}
            engines={@props.engines}
            topEngines={@state.topEngines}
            limitWarningCode={@state.limitWarningCode}
            lastLimitedAt={@state.lastLimitedAt}
            limitedDurations={@props.limitedDurations}
            charLimit={@props.charLimit}
            currentUser={@props.currentUser}
            assets={@props.assets}
            paths={@props.paths}
            i18n={@props.i18n.target}
            selectAlternative={@selectAlternative}
            setActiveSentence={@setActiveSentence}
            setActiveWord={@setActiveWord}
            selectWordAlternative={@selectWordAlternative}
            selectTargetText={@selectTargetText}
            unselectTargetText={@unselectTargetText}
            selectEngine={@selectEngine}
            removeTopEngine={@removeTopEngine}
            scrollToPricing={@scrollToPricing}
            openCheckoutModal={@openCheckoutModal} />

  renderModal: ->
    # Merge the relevant i18n keys
    mergedI18n = {
      ...@props.i18n.modal                         # needed by the "Modal" component
      waitingListForm: @props.i18n.waitingListForm # needed by the shared "WaitingListForm" component
    }

    <Modal environment={@props.environment}
           origin={@state.modalOrigin}
           currentUser={@props.currentUser}
           waitingListFlag={@state.waitingListFlag}
           updateWaitingListFlag={@updateWaitingListFlag}
           currentLocale={@props.currentLocale}
           i18n={mergedI18n} />
